/**
 * Module-level cache for dashboard item refresh results.
 *
 * Two-layer cache:
 * - Main cache: keyed by (itemId, startDate, endDate, timeGrain) — stores main refresh data
 * - Comparison cache: keyed by (itemId, startDate, endDate, compStart, compEnd) — stores comparison data
 *
 * Cache expiry is driven by backend hints:
 * - Managed sources (data_freshness="managed"): 30-min default TTL (data only changes on SQLMesh runs)
 * - Customer sources (data_freshness="ttl"): the backend sends the moment the
 *   source's refresh rule turns the tile stale (`cache_valid_until`, plan
 *   2026-10-21-customer-source-cache-refresh-interval, D7). A ttl answer
 *   without that moment is not cached at all: guessing an expiry here would be
 *   a second rule beside the server's.
 * - Fallback: 2-min TTL for entries without hints (backwards compatibility)
 */

import type { RefreshItemResponse, ComparisonData, DashboardDTO, DashboardItemDTO } from "../api/dashboards";
import type { TimeGrain } from "@/components/dashboards/TimeRangeSelector";
// The stand-in coordinate a target leg is cached under — it has no window
// of its own. One constant, shared with the page that writes the key.
import { TARGET_COMPARISON_KEY, type ComparisonMode } from "@/utils/comparisonWindow";
import { pivotViewerChoiceOn } from "@/components/charts/pivotViewerGrain";
import { lacksTrendData } from "@/utils/trendDataMerge";
import { needsServedSentence } from "@/components/dashboards/composition/smartText/servedSentence";
import { grainForPeriod, ownPeriodRange } from "@/utils/tilePeriod";

const DEFAULT_MANAGED_TTL_MS = 30 * 60 * 1000; // 30 minutes
const DEFAULT_FALLBACK_TTL_MS = 2 * 60 * 1000; // 2 minutes (backwards compat)

type CacheEntry<T> = {
  data: T;
  fetchedAt: number;
  /** Absolute expiry timestamp (ms). null = use managed default (30min from fetchedAt). */
  validUntil: number | null;
};

const mainCache = new Map<string, CacheEntry<RefreshItemResponse>>();
const compCache = new Map<string, CacheEntry<ComparisonData>>();
/**
 * LRU backstop — MUST exceed the overview prefetch's total write volume.
 *
 * The overview hydrates EVERY managed dashboard (structure GET → tab snapshot
 * + every item's 5 prewarmed presets, all tabs). Measured 2026-08-05: Ecom
 * Performance alone writes 286 main-cache entries, Shopify 192; six managed
 * dashboards together exceed 1000. At the old cap of 200, each dashboard's
 * hydration silently evicted the PREVIOUS dashboards' first-tab entries — and
 * often its own (Ecom's tab 5 evicted its tab 1). DashboardCardPreview never
 * fetches by invariant, so an evicted entry meant a permanently blank card:
 * which cards went blank depended on prefetch order and per-dashboard item
 * counts (the blank-Shopify-card bug). Entries are small (KPI rows, ≤31-day
 * slices, ≤25-row tables), so the cap is sized to the working set with
 * headroom rather than trimmed to memory.
 */
const MAX_ENTRIES = 2500;

function makeMainKey(itemId: string, startDate: string, endDate: string, timeGrain?: string): string {
  return `${itemId}:${startDate}:${endDate}:${timeGrain ?? "_"}`;
}

function makeCompKey(
  itemId: string,
  startDate: string,
  endDate: string,
  compStart: string,
  compEnd: string
): string {
  return `${itemId}:${startDate}:${endDate}:${compStart}:${compEnd}`;
}

function isExpired(entry: CacheEntry<unknown>): boolean {
  const now = Date.now();
  if (entry.validUntil !== null) {
    // Explicit expiry from backend (custom/ttl sources)
    return now > entry.validUntil;
  }
  // Managed sources: use generous 30-min default
  return now - entry.fetchedAt > DEFAULT_MANAGED_TTL_MS;
}

function getFromCache<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (isExpired(entry)) {
    cache.delete(key);
    return null;
  }
  // LRU: move to end
  cache.delete(key);
  cache.set(key, entry);
  return entry.data;
}

function setInCache<T>(
  cache: Map<string, CacheEntry<T>>,
  key: string,
  data: T,
  validUntil: number | null,
): void {
  if (cache.size >= MAX_ENTRIES && !cache.has(key)) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey) cache.delete(oldestKey);
  }
  cache.set(key, { data, fetchedAt: Date.now(), validUntil });
}

/**
 * Compute validUntil from a RefreshItemResponse's cache hints.
 * - data_freshness="managed" + cache_valid_until=null → null (30-min default in isExpired)
 * - data_freshness="ttl" + cache_valid_until=ISO string → absolute ms timestamp
 * - data_freshness="ttl" without cache_valid_until → `DO_NOT_CACHE`: the
 *   server names the expiry of every customer answer it sends (D7), so a
 *   missing one is a server that could not compute it, and the browser must
 *   not invent one (it used to guess 4 h).
 * - No hints → fallback 2-min TTL
 */
export const DO_NOT_CACHE = false as const;
export type ValidUntil = number | null | typeof DO_NOT_CACHE;

function computeValidUntil(response: RefreshItemResponse): ValidUntil {
  if (response.data_freshness === "managed") {
    return null; // isExpired() will use 30-min default
  }
  if (response.cache_valid_until) {
    return new Date(response.cache_valid_until).getTime();
  }
  if (response.data_freshness === "ttl") {
    return DO_NOT_CACHE;
  }
  // No hints at all (backwards compat) — use short fallback TTL
  return Date.now() + DEFAULT_FALLBACK_TTL_MS;
}

/** Extract validUntil from a refresh response (for passing to comparison cache). */
export function getValidUntilFromResponse(response: RefreshItemResponse): ValidUntil {
  return computeValidUntil(response);
}

// --- Main cache ---

/** Is this cached answer a metric card the backend marked as waiting for its
 *  live sparkline (`sparkline_pending`, set only on a GET-hydrated spec)? */
export function awaitsLiveSparkline(response: Pick<RefreshItemResponse, "chart_spec_json">): boolean {
  const spec = response.chart_spec_json as Record<string, unknown> | null | undefined;
  return spec != null && spec.sparkline_pending === true;
}

export function getCachedRefresh(
  itemId: string,
  startDate: string,
  endDate: string,
  timeGrain?: string
): RefreshItemResponse | null {
  const hit = getFromCache(mainCache, makeMainKey(itemId, startDate, endDate, timeGrain));
  // A trend table's answer without its trend (every hydration path writes
  // one: the GET, a tab snapshot and a prewarmed range carry no series) is
  // not an answer for this tile: a hit would draw a skeleton no refresh ever
  // replaces. One gate on the READ, so every writer is covered (plan
  // 2026-10-16 table sparkline, S5).
  if (hit && lacksTrendData(hit as Parameters<typeof lacksTrendData>[0])) return null;
  // A metric card the GET hydrated without the sparkline only the refresh
  // can draw (no wide store: a customer dataset or metric). Same reason as
  // the trend above: a hit would draw a card whose sparkline never arrives
  // (plan 2026-10-18 wide cache, phase Y).
  if (hit && awaitsLiveSparkline(hit)) return null;
  return hit;
}

export function setCachedRefresh(
  itemId: string,
  startDate: string,
  endDate: string,
  data: RefreshItemResponse,
  timeGrain?: string
): void {
  // Strip comparison_data before storing in main cache
  const { comparison_data: _, comparison_error: __, target_data: ___, target_error: ____, ...mainData } = data;
  const validUntil = computeValidUntil(data);
  // A successful refresh ends the failure streak for this key
  // ("consecutive" semantics — see failure backoff above), cached or not.
  failCache.delete(makeMainKey(itemId, startDate, endDate, timeGrain));
  if (validUntil === DO_NOT_CACHE) return;
  setInCache(mainCache, makeMainKey(itemId, startDate, endDate, timeGrain), mainData as RefreshItemResponse, validUntil);
}

// --- Comparison cache ---

export function getCachedComparison(
  itemId: string,
  startDate: string,
  endDate: string,
  compStart: string,
  compEnd: string
): ComparisonData | null {
  return getFromCache(compCache, makeCompKey(itemId, startDate, endDate, compStart, compEnd));
}

/**
 * Every requested comparison leg cached for this item and main range — the
 * keys come from `comparisonLegCacheKeys(getComparisonRequest(range))`. An
 * empty list (no comparison) is trivially cached.
 */
export function comparisonLegsCached(
  itemId: string,
  startDate: string,
  endDate: string,
  legKeys: ReadonlyArray<{ start: string; end: string }>,
): boolean {
  return legKeys.every(
    (k) => getCachedComparison(itemId, startDate, endDate, k.start, k.end) !== null,
  );
}

export function setCachedComparison(
  itemId: string,
  startDate: string,
  endDate: string,
  compStart: string,
  compEnd: string,
  data: ComparisonData,
  validUntil?: ValidUntil,
): void {
  if (validUntil === DO_NOT_CACHE) return;
  setInCache(compCache, makeCompKey(itemId, startDate, endDate, compStart, compEnd), data, validUntil ?? null);
}

// --- Failure backoff (plan: 2026-07-15-failing-tile-retry-backoff) ---
//
// Tiles that fail persistently (e.g. missing mart table) never enter
// mainCache, so every refresh trigger re-fires a live BQ attempt for
// them. After MAX_AUTO_REFRESH_FAILURES consecutive failures for a
// (item, range, grain) key, AUTOMATIC retries pause for the cooldown.
// Manual per-tile refresh bypasses + resets; a successful refresh, a
// watermark advance (fresh data), or a full cache clear also reset.

export const MAX_AUTO_REFRESH_FAILURES = 3;
export const FAIL_BACKOFF_MS = 15 * 60 * 1000; // ~managed cache TTL

type FailEntry = { count: number; pausedUntil: number | null };
const failCache = new Map<string, FailEntry>();

export function recordRefreshFailure(
  itemId: string,
  startDate: string,
  endDate: string,
  timeGrain?: string,
): void {
  const key = makeMainKey(itemId, startDate, endDate, timeGrain);
  const entry = failCache.get(key) ?? { count: 0, pausedUntil: null };
  entry.count += 1;
  if (entry.count >= MAX_AUTO_REFRESH_FAILURES) {
    entry.pausedUntil = Date.now() + FAIL_BACKOFF_MS;
  }
  failCache.set(key, entry);
}

export function isRefreshBackedOff(
  itemId: string,
  startDate: string,
  endDate: string,
  timeGrain?: string,
): boolean {
  const key = makeMainKey(itemId, startDate, endDate, timeGrain);
  const entry = failCache.get(key);
  if (!entry || entry.pausedUntil === null) return false;
  if (Date.now() >= entry.pausedUntil) {
    // Cooldown expired — next trigger gets a fresh set of attempts.
    failCache.delete(key);
    return false;
  }
  return true;
}

export function resetRefreshFailures(itemId: string): void {
  for (const key of [...failCache.keys()]) {
    if (key.startsWith(`${itemId}:`)) failCache.delete(key);
  }
}

// --- Invalidation ---

export function invalidateItem(itemId: string): void {
  for (const key of [...mainCache.keys()]) {
    if (key.startsWith(`${itemId}:`)) mainCache.delete(key);
  }
  for (const key of [...compCache.keys()]) {
    if (key.startsWith(`${itemId}:`)) compCache.delete(key);
  }
  // Fresh data may have resolved the failure — resume retries.
  resetRefreshFailures(itemId);
}

export function clearDashboardCache(): void {
  mainCache.clear();
  compCache.clear();
  failCache.clear();
  reportedIncoherent.clear();
}

// --- Watermark-driven invalidation (Plan: 2026-05-19-server-side-...) ---

// Last-seen `last_cache_refill_at` per data_source. Piggyback target for
// the 30s notifications poll: when the backend's watermark advances, we
// wipe the affected items' cache entries so the next dashboard load
// pulls fresh data through the existing hydrate path.
//
// First-poll-after-bootstrap is a baseline write: we already hydrated
// fresh data from the dashboard GET, so don't double-invalidate.
const seenWatermarks = new Map<string, string>();
let watermarkBootstrapDone = false;

/**
 * Item-to-data-source lookup. Populated by `hydrateFromDashboard` as a
 * byproduct of iterating the dashboard structure. Used by
 * `applyCacheWatermarks` to map an advanced DS watermark back to the
 * cache keys it should invalidate.
 *
 * Keyed by item public_id; value is the data_source public_id (or null
 * for items not bound to a managed DS — e.g., user/workspace dashboards).
 */
const itemToDataSource = new Map<string, string | null>();

export function registerItemDataSource(itemId: string, dsPublicId: string | null): void {
  itemToDataSource.set(itemId, dsPublicId);
}

/**
 * Compare incoming `cache_watermarks` (from the notifications poll
 * response) against last-seen values. For any DS whose watermark
 * advanced, invalidate every cache entry for items bound to that DS.
 *
 * Returns the list of data_source public_ids that were invalidated.
 * Caller can use this to trigger an active-dashboard refresh if the
 * current view is bound to one of them.
 *
 * Bootstrap rule: the FIRST call after page load only records baselines
 * without invalidating — we just hydrated fresh data from the dashboard
 * GET and don't want to immediately throw it away.
 */
export function applyCacheWatermarks(
  watermarks: Record<string, string>,
): string[] {
  if (!watermarkBootstrapDone) {
    for (const [dsId, ts] of Object.entries(watermarks)) {
      seenWatermarks.set(dsId, ts);
    }
    watermarkBootstrapDone = true;
    return [];
  }

  const advanced: string[] = [];
  for (const [dsId, ts] of Object.entries(watermarks)) {
    const prev = seenWatermarks.get(dsId);
    if (prev !== ts) {
      seenWatermarks.set(dsId, ts);
      // Wipe cache entries for items on this DS.
      let invalidatedCount = 0;
      for (const [itemId, mappedDsId] of itemToDataSource) {
        if (mappedDsId === dsId) {
          invalidateItem(itemId);
          invalidatedCount++;
        }
      }
      if (invalidatedCount > 0) {
        advanced.push(dsId);
      }
    }
  }
  return advanced;
}

// DEV-only: reset bootstrap state. Used by tests.
export function _resetWatermarkBootstrap(): void {
  seenWatermarks.clear();
  itemToDataSource.clear();
  watermarkBootstrapDone = false;
}

// ============================================================================
// HYDRATION FROM DASHBOARD GET RESPONSE
// Plan: docs/plans-pending/2026-05-18-hydrate-dashboard-cache-from-get-response.md
// ============================================================================

// Grain-normalisation: templates write different vocabularies into IR.
// composite_marketing_sales.py uses "day"/"week"/...; legacy templates use
// "daily"/"weekly"/.... Both map to the frontend TimeGrain enum. Without
// normalisation, grain-match check `irGrain === effectiveTimeGrain` would
// silently miss for composite items.
const GRAIN_MAP: Record<string, TimeGrain> = {
  day: "daily",
  week: "weekly",
  month: "monthly",
  quarter: "quarterly",
  year: "yearly",
  daily: "daily",
  weekly: "weekly",
  monthly: "monthly",
  quarterly: "quarterly",
  yearly: "yearly",
};

function normalizeGrain(g: unknown): TimeGrain | null {
  if (typeof g !== "string") return null;
  return GRAIN_MAP[g] ?? null;
}

// ---------------------------------------------------------------------------
// Spec coherence: y_keys must resolve against the rows they are cached with
//
// The legend reads `spec.y_keys`; the marks read `row[y_key]`. Nothing in the
// render path cross-checks the two, so a spec whose y_keys name columns no row
// has renders as an entirely empty chart under a fully populated legend —
// plausible-looking, never an error. That is exactly what the two hydration
// paths produced when they pasted RAW long-form rows over a spec whose y_keys
// the backend pivot had rewritten (fixed in c88ab3359 / 407176b24; measured
// 2026-08-26 as 5 tiles across 4 dashboards, unnoticed for weeks).
//
// The check therefore lives where the incoherent spec is ASSEMBLED, not where
// it is rendered: here the message can name the writer that produced it. A
// render-time guard fires one layer later with no idea which path is at fault.
// ---------------------------------------------------------------------------

/**
 * Counts-only report on whether a chart spec's `y_keys` resolve against its
 * rows. Deliberately carries NO names and NO values: for the breakdown family
 * the y_key names ARE customer business data (campaign names, channel names),
 * and rows are customer data throughout.
 */
export type YKeyCoherenceReport = {
  /**
   * Whether the check applies at all. False when there is no spec object, no
   * non-empty `y_keys` array, or no rows — empty rows is a valid state ("no
   * data in this range") and must never be reported as a violation.
   */
  applicable: boolean;
  /** True when the check does not apply, or when every y_key is in every row. */
  coherent: boolean;
  /** Number of `y_keys` entries; 0 when there is no usable y_keys array. */
  yKeyCount: number;
  /** How many of those entries are missing from at least one row. */
  absentCount: number;
  /** Rows examined. */
  rowCount: number;
};

const NOT_APPLICABLE: YKeyCoherenceReport = {
  applicable: false,
  coherent: true,
  yKeyCount: 0,
  absentCount: 0,
  rowCount: 0,
};

/**
 * Is this spec's `y_keys` set resolvable against its own rows?
 *
 * Exported so the production guard below and the test suite's
 * `expectYKeysResolvable` share ONE definition of "coherent" and cannot drift
 * apart — a guard that tolerates what the tests reject (or vice versa) is
 * worse than no guard at all.
 *
 * Rows are read from `rows`, falling back to `data` when `rows` is absent:
 * the chokepoint writes both, and the chart families read one or the other.
 * Never throws — a malformed spec reports "not applicable".
 */
export function checkYKeyCoherence(spec: unknown): YKeyCoherenceReport {
  if (!spec || typeof spec !== "object") return NOT_APPLICABLE;
  const s = spec as Record<string, unknown>;
  const yKeys = s.y_keys;
  if (!Array.isArray(yKeys) || yKeys.length === 0) return NOT_APPLICABLE;
  const rows = Array.isArray(s.rows)
    ? s.rows
    : Array.isArray(s.data)
      ? s.data
      : null;
  // No rows at all, or an empty range: nothing to contradict the y_keys.
  if (rows === null || rows.length === 0) {
    return { ...NOT_APPLICABLE, yKeyCount: yKeys.length };
  }
  let absent = 0;
  for (const key of yKeys) {
    const name = typeof key === "string" ? key : String(key);
    const inEveryRow = rows.every(
      (row) =>
        !!row &&
        typeof row === "object" &&
        Object.prototype.hasOwnProperty.call(row, name),
    );
    if (!inEveryRow) absent += 1;
  }
  return {
    applicable: true,
    coherent: absent === 0,
    yKeyCount: yKeys.length,
    absentCount: absent,
    rowCount: rows.length,
  };
}

/**
 * DEV-only observation on a spec about to be written into mainCache.
 *
 * - Wrapped in `import.meta.env.DEV` so the whole body is statically
 *   eliminated from production builds.
 * - Logs counts, indices and the item id ONLY — never a y_key name and never
 *   a row value (customer business data).
 * - Observes; it does not throw and does not change what gets cached. The
 *   try/catch means even a pathological spec cannot break hydration.
 */
/**
 * Already-reported (writer, item, shape) triples.
 *
 * Hydration runs many times per session — tab switches, prefetch, re-renders —
 * and each pass rewrites the same entry. Measured on dev: one stale tile
 * produced ten identical lines before this. A finding repeated ten times is
 * not ten findings; it buries the second, different one. Cleared with the
 * caches so an account switch reports afresh.
 */
const reportedIncoherent = new Set<string>();

function reportIncoherentSpec(writer: string, itemId: string, spec: unknown): void {
  if (import.meta.env.DEV) {
    try {
      const report = checkYKeyCoherence(spec);
      if (report.applicable && !report.coherent) {
        // Keyed on the counts too: if the same tile later fails differently,
        // that is a new finding and should be said out loud.
        const seen = `${writer}|${itemId}|${report.absentCount}/${report.yKeyCount}`;
        if (reportedIncoherent.has(seen)) return;
        reportedIncoherent.add(seen);
        console.error(
          `[hydrate] ${writer}: ${report.absentCount} of ${report.yKeyCount} ` +
            `y_keys absent from rows (item=${itemId}, rows=${report.rowCount}) — ` +
            `this tile will render an empty chart under a full legend. ` +
            `Names and values withheld: y_key names are customer data.`,
        );
      }
    } catch {
      // Observation must never cost a render.
    }
  }
}

// Hydration freshness gate for a managed tile — pure safety net. Primary
// invalidation happens server-side on SQLMesh transform SUCCESS (nulls
// cached_data_json + last_refreshed_at, see internal_routes.py
// `/transforms/complete`). This TTL only matters if the hook didn't fire.
// 25h covers one daily cycle with buffer; mirrors backend WIDE_TTL_SECONDS.
const MANAGED_HYDRATION_TTL_MS = 25 * 60 * 60 * 1000;

type HydrationRule = Pick<HydrateContext, "tierTtlMs" | "dataFreshness" | "cacheValidUntil">;

/**
 * How long the browser may serve what the GET carries for this tile, per
 * TILE (plan 2026-10-21-customer-source-cache-refresh-interval, D7):
 *
 * - A tile with `cache_valid_until` is on a customer source: the server's
 *   rule (the source's refresh interval, change detection, a Refresh data
 *   press) decides, and the browser keeps it exactly that long.
 * - A tile bound to a source on a dashboard whose source is a customer's,
 *   but without that moment, had its hint fail open on the server: `null`,
 *   do not hydrate. Guessing would be the old 5 min / 60 s rule again.
 * - Everything else (managed and DS-less tiles) keeps the managed rule.
 */
function hydrationRuleFor(item: DashboardItemDTO, dashboard: DashboardDTO): HydrationRule | null {
  if (item.cache_valid_until) {
    return { tierTtlMs: null, dataFreshness: "ttl", cacheValidUntil: item.cache_valid_until };
  }
  if (isCustomerTileWithoutHint(item, dashboard)) return null;
  return { tierTtlMs: MANAGED_HYDRATION_TTL_MS, dataFreshness: "managed", cacheValidUntil: null };
}

function isCustomerTileWithoutHint(item: DashboardItemDTO, dashboard: DashboardDTO): boolean {
  return (
    !item.cache_valid_until
    && item.data_source_id != null
    && dashboard.data_source?.ownership_mode === "customer"
  );
}

/** A tile the server serves under a customer source's rule: it carries that
 *  rule's moment, or sits on a customer-source dashboard bound to a source.
 *  The dashboard's "Refresh data" entry shows only when one exists. */
export function isCustomerSourceTile(item: DashboardItemDTO, dashboard: DashboardDTO): boolean {
  return !!item.cache_valid_until || isCustomerTileWithoutHint(item, dashboard);
}

/**
 * Hydrate mainCache + compCache synchronously from a dashboard GET response.
 *
 * Goal: when the user hard-refreshes a dashboard page, the GET response
 * already ships `cached_data_json` and (when comparison_mode is set)
 * `comparison_cached_data_json` per item — fully derived from the backend
 * wide cache in ~150ms. Without this hydration the in-memory caches are
 * empty and the unified-effect's refreshAllItems falls through to API
 * roundtrips, showing the spinner overlay for ~2s. After hydration, those
 * items hit the synchronous cache-check phase and render instantly.
 *
 * Eligibility gates (each gate produces a cache-skip for that item; the
 * existing refresh flow then handles it normally):
 *  - cached_data_json missing or has no `rows`.
 *  - last_refreshed_at missing; for a managed tile older than 25 h — prevents
 *    stale-data masking auto-refresh.
 *  - a customer-source tile: its own `cache_valid_until` (the server's rule)
 *    missing or already past (`hydrationRuleFor`).
 *  - query_ir_json missing start_date / end_date.
 *  - intent="time-series" / "trend" / "breakdown" without `time_grain`,
 *    or with a grain that doesn't match effectiveTimeGrain after
 *    normalisation (silent-corruption-prevention).
 *  - When `comparisonMode !== "none"`: comparison_cached_data_json must
 *    also be present and non-empty (all-or-nothing rule).
 *
 * Grain-less items (no `time_grain` in IR — single-value, categorical)
 * are written under EVERY entry in `availableTimeGrains` so that a
 * subsequent `setAvailableTimeGrains` mutation (which would re-memo
 * `effectiveTimeGrain`) still finds a cache hit.
 *
 * Errors are caught and logged; hydration MUST NOT break dashboard load.
 */
export function hydrateFromDashboard(
  dashboard: DashboardDTO,
  effectiveTimeGrain: TimeGrain,
  availableTimeGrains: TimeGrain[],
  comparisonMode: ComparisonMode,
  /** The plan as a second leg beside a window mode (W1.8; ADR-0267): the
   *  GET was asked `with_target`, so an item may carry
   *  `target_cached_data_json`, hydrated under TARGET_COMPARISON_KEY. */
  withTarget: boolean = false,
): void {
  try {
    // Populate the item → data_source map so watermark-driven
    // invalidation (applyCacheWatermarks) can locate cache keys to wipe
    // when a managed-pipeline refill completes.
    const dashDataSourceId = dashboard.data_source?.public_id ?? null;

    for (const topic of dashboard.topics) {
      for (const tab of topic.tabs) {
        for (const item of tab.items) {
          registerItemDataSource(item.id, dashDataSourceId);
          const rule = hydrationRuleFor(item, dashboard);
          // A customer tile the server gave no expiry (its hint failed open):
          // nothing to hydrate under, so the refresh flow fetches it.
          if (rule === null) continue;
          // A block with its own period (ADR-0445) was served over ITS window,
          // at its automatic grain, with its own comparison (the GET's
          // `comparison_for_item`): hydrated under those, never the header's.
          const own = ownPeriodRange(item.period);
          hydrateItem(item, {
            effectiveTimeGrain: own ? grainForPeriod(own, "auto", availableTimeGrains) : effectiveTimeGrain,
            availableTimeGrains,
            comparisonMode: own ? own.comparisonMode : comparisonMode,
            withTarget: own ? own.withTarget === true : withTarget,
            ...rule,
          });
          // Prewarmed preset snapshots — one mainCache entry per preset.
          // Header windows only: a block with its own period has none.
          // Makes date-switches to yesterday/this_week/last_week/
          // this_month/last_month instant cache-hits instead of
          // bulk-refresh roundtrips. Written by the backend refill from
          // the wide cache (services/dashboard_refresh_service.py
          // _build_prewarmed_ranges) for MANAGED sources only, so a
          // customer tile never has any (refresh-interval plan, step 4).
          if (rule.dataFreshness === "managed") {
            hydratePrewarmedRanges(item);
          }
        }
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error("[hydrate] threw, dashboard load not affected:", err);
    }
  }
}

/**
 * Hydrate mainCache + compCache from a tab snapshot — the materialized
 * (tab, preset) payload written by the backend refill (page-level cache
 * plan). Called for the GET-embedded snapshot on load AND for page-endpoint
 * responses on tab/preset switches. After hydration, refreshAllItems'
 * Phase 1 cache-check hits for every covered item; uncovered items fall
 * through to the small per-tile bulk-refresh (hybrid render).
 *
 * Writes BOTH comparison modes to compCache so switching comparison mode
 * stays an instant hit.
 *
 * `p.chart_spec_json` is taken AS-IS and is authoritative: the backend
 * injects the preset's rows into the spec before running the response
 * chokepoint (`build_response_chart_spec`), so the snapshot already carries
 * this range's rows in the shape the chart reads — pivoted, with `y_keys`
 * rewritten, for the breakdown / funnel / metric-bar families. Overwriting
 * `rows`/`data` here with the raw `cached_data_json.rows` is exactly the bug
 * that rendered pivoted tiles as an empty chart under a full legend. Empty
 * rows in the spec are valid ("no data in range") and render the tile's
 * no-data state, same as a live bulk-refresh would.
 */
// `PRESENTATION_SPEC_KEYS` and `overlayPresentationKeys` moved to
// `@verity/charts` on 2026-09-13 (plan one-table-model, B1b): the report
// canvas overlays a draft with the same list, and the drift test against the
// backend's `PRESENTATION_SPEC_KEYS` reads one constant. Re-exported here so
// no importer changes.
export { PRESENTATION_SPEC_KEYS, overlayPresentationKeys } from "@verity/charts";
import { overlayPresentationKeys } from "@verity/charts";

export function hydrateFromTabSnapshot(
  snapshot: import("../api/dashboards").TabSnapshotDTO,
  dashboard: DashboardDTO,
): void {
  try {
    // A tab snapshot is written by the managed refill only: the server
    // serves one only with a refill watermark, which a customer source has
    // not (refresh-interval plan, step 5). So every entry here is under the
    // managed rule, and a customer tile is skipped below as a guard: its
    // expiry is the server's per-tile rule, never this snapshot's.
    const validUntil = Date.now() + MANAGED_HYDRATION_TTL_MS;

    // The snapshot is DATA baked at refill time; presentation keys a user sets
    // on the tile afterwards (plan 2026-09-03 table column format overrides)
    // live on the stored spec the dashboard DTO carries. Overlay them, so a
    // format set an hour ago does not wait for the next refill — and a Reset
    // removes a stale one the snapshot still bakes.
    const storedSpecById = new Map<string, Record<string, unknown>>();
    const servedSentenceIds = new Set<string>();
    // The snapshot is the HEADER preset's window; a block with its own period
    // is never drawn from it (ADR-0445; the refill leaves it out).
    const ownPeriodIds = new Set<string>();
    const customerIds = new Set<string>();
    for (const topic of dashboard.topics ?? []) {
      for (const tab of topic.tabs ?? []) {
        for (const item of tab.items ?? []) {
          storedSpecById.set(item.id, (item.chart_spec_json ?? {}) as Record<string, unknown>);
          if (needsServedSentence(item)) servedSentenceIds.add(item.id);
          if (item.period) ownPeriodIds.add(item.id);
          if (isCustomerSourceTile(item, dashboard)) customerIds.add(item.id);
        }
      }
    }

    for (const [itemId, p] of Object.entries(snapshot.items)) {
      if (!p || !p.cached_data_json) continue;
      // A smart text tile's snapshot has data and no sentence (plan
      // 2026-10-16-a-smart-text-block, S5): it takes the single refresh. So
      // does a tile whose subtitle has variables (2026-10-16-a-block-subtitle).
      if (servedSentenceIds.has(itemId) || ownPeriodIds.has(itemId) || customerIds.has(itemId)) continue;
      const payload = {
        chart_spec_json: overlayPresentationKeys(
          {
            ...(p.chart_spec_json as Record<string, unknown>),
            time_grain: snapshot.time_grain ?? undefined,
          },
          storedSpecById.get(itemId),
        ),
        chart_config_json: p.chart_config_json,
        data_json: p.cached_data_json,
        cached_data_json: p.cached_data_json,
        row_count: p.row_count,
        row_count_truncated: p.row_count_truncated,
        last_refreshed_at: snapshot.refreshed_at,
        cache_hit: true,
        data_freshness: "managed",
        cache_valid_until: null,
      } as unknown as import("../api/dashboards").RefreshItemResponse;

      reportIncoherentSpec("hydrateFromTabSnapshot", itemId, payload.chart_spec_json);

      setInCache(
        mainCache,
        makeMainKey(
          itemId,
          snapshot.start_date,
          snapshot.end_date,
          snapshot.time_grain ?? undefined,
        ),
        payload,
        validUntil,
      );

      for (const comp of [p.comparison_previous_year, p.comparison_previous_period]) {
        if (comp && comp.date_range) {
          setCachedComparison(
            itemId,
            snapshot.start_date,
            snapshot.end_date,
            comp.date_range.start_date,
            comp.date_range.end_date,
            comp,
            null,
          );
        }
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error("[hydrate] tab-snapshot hydration threw:", err);
    }
  }
}


/**
 * Hydrate every item's pre-warmed preset snapshots — the overview's
 * counterpart to what `hydrateFromDashboard` does for an opened dashboard.
 *
 * `GET /dashboards/{id}` ships `prewarmed_ranges_json` on each item: the five
 * `WARMED_PRESETS` (yesterday, this/last week, this/last month), rows and a
 * previous-year comparison included, all assembled server-side from the wide
 * cache at zero BigQuery cost. The overview's prefetch used to hydrate only
 * `tab_snapshot` and discard the rest, so changing the period on the overview
 * would re-request data the browser was already holding.
 *
 * Unlike `hydrateFromDashboard` this takes no grain/comparison context: each
 * prewarmed entry carries its own `time_grain` and is written under exactly
 * that key, which is the same key `getPreviewRange` resolves to for that
 * preset (both sides mirror `compute_default_grain`).
 *
 * Never throws — a failed hydration must cost a request, not a render.
 */
export function hydratePrewarmedRangesForDashboard(dashboard: DashboardDTO): void {
  try {
    // Prewarmed ranges are written by the managed refill only (refresh-
    // interval plan, step 4), so they are hydrated under the managed rule,
    // and a customer tile is skipped as a guard (its expiry is the server's
    // per-tile rule).
    const dashDataSourceId = dashboard.data_source?.public_id ?? null;

    for (const topic of dashboard.topics) {
      for (const tab of topic.tabs) {
        for (const item of tab.items) {
          // Keep watermark-driven invalidation able to find these entries:
          // a managed refill that completes must be able to wipe what we
          // just wrote, exactly as it can for hydrateFromDashboard's.
          registerItemDataSource(item.id, dashDataSourceId);
          if (isCustomerSourceTile(item, dashboard)) continue;
          hydratePrewarmedRanges(item);
        }
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error("[hydrate] prewarmed-range hydration threw:", err);
    }
  }
}


/** Managed tiles only (the managed refill is the one writer): every entry
 *  is kept for the managed hydration TTL. */
function hydratePrewarmedRanges(item: DashboardItemDTO): void {
  const presets = item.prewarmed_ranges_json;
  if (!presets) return;
  // Every entry is a HEADER preset's window; a block with its own period
  // never draws one (ADR-0445). The refill writes none for it — this keeps a
  // stale one from before the period was set off the tile as well.
  if (item.period) return;
  // A smart text tile never reads the browser cache (see hydrateItem), nor
  // does a tile whose subtitle has variables.
  if (needsServedSentence(item)) return;
  for (const [_presetName, snap] of Object.entries(presets)) {
    if (!snap || !snap.start_date || !snap.end_date) continue;
    // `snap.pivot` carries the chart-spec fields the response chokepoint
    // owns (rows/data, and for breakdown tiles the rewritten y_keys),
    // rendered server-side for THIS preset's rows. It is written for every
    // entry, pivot family or not, so this is a pure presence test — never
    // re-derive the pivot trigger here, the IR-based rule lives in one
    // place (backend `_preset_chart_spec_fields`).
    //
    // Absent = an entry persisted before that shipped. Skip the whole
    // preset: the date-switch then costs one bulk-refresh roundtrip, which
    // renders correctly. Writing the old shape instead (item-level spec +
    // raw rows pasted over it) leaves a breakdown tile's y_keys naming
    // columns no row has — an empty chart under a fully populated legend.
    if (!snap.pivot) continue;
    // Empty rows are valid: they represent "no data in this range"
    // (e.g., marts lag behind for yesterday/this_week). `pivot.set` then
    // carries empty rows too, and the tile renders its no-data state just
    // like a bulk-refresh would.
    const rows = snap.rows ?? [];

    // Also write compCache for BOTH shifted legs, each under the window the
    // server computed it over (its `date_range`), so the refreshAllItems
    // Phase 1 cache-check passes under either window mode. Without this,
    // the all-or-nothing comparison rule forces a bulk-refresh roundtrip on
    // every date-switch. Keyed by the server's window: when it differs from
    // the client's `comparisonWindowFor`, the lookup misses — never a hit on
    // the wrong window. A requested plan leg is not here and still makes the
    // tile a miss (`comparisonLegsCached`, S2).
    for (const leg of [snap.comparison_previous_year, snap.comparison_previous_period]) {
      if (!leg || !leg.date_range) continue;
      setCachedComparison(
        item.id,
        snap.start_date,
        snap.end_date,
        leg.date_range.start_date,
        leg.date_range.end_date,
        leg,
        null,
      );
    }
    // Build a RefreshItemResponse-shaped payload matching what a real
    // bulk-refresh response would produce. The mainCache `getCachedRefresh`
    // lookup keys on (itemId, startDate, endDate, timeGrain).
    //
    // Spec assembly order mirrors the server: spread the item-level spec
    // (titles, axis labels, chart_type — everything the chokepoint does not
    // touch), apply the chokepoint's output for this preset, then remove
    // the keys it dropped. `data_json`/`cached_data_json` keep the RAW rows
    // — that is the tabular payload, and `snap.columns` describes it.
    const spec: Record<string, unknown> = {
      ...(item.chart_spec_json as Record<string, unknown>),
      ...snap.pivot.set,
      time_grain: snap.time_grain ?? undefined,
    };
    for (const key of snap.pivot.drop ?? []) {
      delete spec[key];
    }
    // The GET's "waiting for a live sparkline" mark is about the item's OWN
    // range; inherited here it would turn every preset of the card into a
    // miss (`getCachedRefresh`). A preset is a store answer for its window,
    // served as is (Opus review of phase Y, MIN 4).
    delete spec.sparkline_pending;
    // `raw_rows` is chokepoint-owned AND range-dependent, but it is
    // deliberately absent from `pivot.set`: it is the long-form row list, and
    // shipping it would write those rows a second time into the same JSONB
    // blob (backend `_CHOKEPOINT_SPEC_KEYS`, pinned by
    // test_preset_fields_never_persist_raw_rows). So the spread inherits the
    // ITEM-LEVEL value — and that is not the raw column: `_item_to_dto` runs
    // the chokepoint before serving, so the DTO carries `raw_rows` for the
    // item's own default range. Measured on dev: 11 monthly rows starting
    // 2026-01-01 while the tile shows last week, which the metric dropdown
    // (ChartWithControls: `metric_options.length >= 2 && spec.raw_rows`) then
    // filters — a dropdown over the wrong period, not a missing one.
    //
    // The reconstruction is exact rather than approximate: chart_formatter
    // sets `raw_rows` to precisely the rows it was handed, and for this entry
    // those are `snap.rows`. Presence is a pure inherited-key test — the
    // dropdown's existence is decided by `metric_options`, which only the
    // creation-time formatter writes and which is range-invariant, so a tile
    // that has no `raw_rows` to begin with must not acquire one here.
    if ("raw_rows" in spec) {
      // Empty rows mirror the chokepoint's `empty_rows` early return, which
      // sets no `raw_rows` at all.
      if (rows.length > 0) spec.raw_rows = rows;
      else delete spec.raw_rows;
    }
    const payload = {
      chart_spec_json: spec,
      chart_config_json: item.chart_config_json,
      data_json: { columns: snap.columns, rows, row_count: snap.row_count, row_count_truncated: snap.row_count_truncated },
      cached_data_json: { columns: snap.columns, rows, row_count: snap.row_count, row_count_truncated: snap.row_count_truncated },
      row_count: snap.row_count,
      row_count_truncated: snap.row_count_truncated,
      last_refreshed_at: item.last_refreshed_at,
      cache_hit: true,
      data_freshness: "managed",
      cache_valid_until: null,
    } as unknown as import("../api/dashboards").RefreshItemResponse;
    const validUntil = Date.now() + MANAGED_HYDRATION_TTL_MS;
    reportIncoherentSpec("hydratePrewarmedRanges", item.id, spec);
    setInCache(
      mainCache,
      makeMainKey(item.id, snap.start_date, snap.end_date, snap.time_grain ?? undefined),
      payload,
      validUntil,
    );
  }
}

type HydrateContext = {
  effectiveTimeGrain: TimeGrain;
  availableTimeGrains: TimeGrain[];
  comparisonMode: ComparisonMode;
  /** See `hydrateFromDashboard`. */
  withTarget?: boolean;
  /** Max age of the item's stamp; null = no age gate (a customer tile, whose
   *  `cacheValidUntil` is the server's whole rule). */
  tierTtlMs: number | null;
  dataFreshness: "managed" | "ttl" | null;
  cacheValidUntil: string | null;
};

/**
 * Hydrate a single item. Returns null on success, or a short reason
 * string identifying which gate skipped the item (used for DEV-mode
 * diagnostics in hydrateFromDashboard).
 */
function hydrateItem(item: DashboardItemDTO, ctx: HydrateContext): string | null {
  // Skip-criteria — empty cache, missing freshness anchor, missing
  // cached_date_range.
  const cached = item.cached_data_json;
  if (!cached) return "no_cached_data_json";
  const rows = (cached as { rows?: unknown[] }).rows;
  if (!Array.isArray(rows) || rows.length === 0) return "empty_rows";
  if (!item.last_refreshed_at) return "no_last_refreshed_at";
  // A pivot whose viewers may choose its period grain needs the server's
  // `pivot_grain` (the grains its Period button offers), which only a
  // refresh response carries, never this GET (plan 2026-10-06 pivot period
  // grain, D3). Hydrated, the tile would draw without its button until some
  // later refresh; left to the refresh, the server answers it from its own
  // cache.
  if (pivotViewerChoiceOn(item.chart_spec_json)) return "pivot_viewer_choice_needs_refresh";
  // A smart text tile's sentence comes only with the single-item refresh
  // (plan 2026-10-16-a-smart-text-block, S5): hydrated, it would have data
  // and no sentence. A subtitle with variables likewise (plan 2026-10-16-a-
  // block-subtitle, V3); the reason string is kept for both.
  if (needsServedSentence(item)) return "smart_text_needs_refresh";

  // cached_date_range carries the EFFECTIVE dates of cached_data_json
  // (updated by refresh), distinct from query_ir_json.start_date/end_date
  // which is the frozen creation-time IR. Using IR dates here produces a
  // silent cache-key miss when the user opens the page with the default
  // calendar "last_month" range, because IR's dates were set when the
  // item was created via chat — almost never the same as the user's view.
  const cdr = item.cached_date_range;
  if (!cdr) return "no_cached_date_range";
  const cachedStart = cdr.start_date;
  const cachedEnd = cdr.end_date;
  if (typeof cachedStart !== "string" || typeof cachedEnd !== "string") {
    return "cdr_dates_not_strings";
  }

  // Freshness: don't hydrate data older than tier TTL — that would mask
  // legitimate auto-refresh. For managed sources the gate is 25h (one
  // daily SQLMesh cycle). A customer tile has no age gate: the server's
  // `cache_valid_until` is its rule, and one already past is not hydrated.
  const refreshedAt = new Date(item.last_refreshed_at).getTime();
  if (Number.isNaN(refreshedAt)) return "last_refreshed_at_nan";
  const ageMs = Date.now() - refreshedAt;
  if (ctx.tierTtlMs !== null && ageMs > ctx.tierTtlMs) {
    return `stale_${Math.round(ageMs / 60_000)}min_vs_${Math.round(ctx.tierTtlMs / 60_000)}min`;
  }
  if (ctx.cacheValidUntil !== null) {
    const validUntilMs = new Date(ctx.cacheValidUntil).getTime();
    if (Number.isNaN(validUntilMs) || validUntilMs <= Date.now()) return "cache_valid_until_passed";
  }

  // Grain gate: hydrate under effectiveTimeGrain for grain-less items
  // (no time_grain on the cached range — single-value / categorical),
  // OR under cachedGrain when it matches effectiveTimeGrain after
  // normalisation.
  const cachedGrain = normalizeGrain(cdr.time_grain);
  let grainsToWrite: TimeGrain[];
  if (cachedGrain === null) {
    // Grain-less item — write under all available grains so that a
    // subsequent setAvailableTimeGrains mutation doesn't miss.
    grainsToWrite =
      ctx.availableTimeGrains.length > 0 ? ctx.availableTimeGrains : [ctx.effectiveTimeGrain];
  } else {
    if (cachedGrain !== ctx.effectiveTimeGrain) {
      // Grain-mismatch on grain-bearing item — skip to avoid silent
      // corruption (data is aggregated at cachedGrain, UI expects
      // effectiveTimeGrain).
      return `grain_mismatch_cdr=${cachedGrain}_eff=${ctx.effectiveTimeGrain}`;
    }
    grainsToWrite = [ctx.effectiveTimeGrain];
  }

  // Intent vocabulary defence-in-depth: log unknown intents in DEV for
  // observability. Production templates use:
  //   "time-series" | "categorical" | "single-value" (legacy)
  //   "trend" | "breakdown" (composite Marketing & Sales)
  if (import.meta.env.DEV) {
    const intent = (item.query_ir_json as Record<string, unknown> | null)?.intent;
    if (intent !== undefined) {
      const known = new Set([
        "time-series", "categorical", "single-value", "trend", "breakdown",
      ]);
      if (typeof intent === "string" && !known.has(intent)) {
        console.warn(`[hydrate] unknown intent=${intent} item=${item.id}`);
      }
    }
  }

  // Comparison data: all-or-nothing rule. When comparison mode is on AND
  // comparison_cached_data_json is missing, skip the entire item —
  // otherwise the mainCache hit + compCache miss path in refreshAllItems
  // still falls through to needsApi, defeating hydration.
  //
  // Empty rows (rows.length === 0) is NOT a skip condition: the backend
  // returns an empty-but-valid sentinel for intents that don't overlay
  // comparison (categorical, breakdown) — see compute_comparison_for_get
  // in dashboard_refresh_service.py. Writing the sentinel to compCache
  // keeps Phase 1's cache-hit check (main + comp) successful, and
  // mergeComparisonIntoSpec renders the empty overlay as "no overlay".
  let comparisonShape: ComparisonData | null = null;
  // compCache coordinates for this item's comparison entry: the ISO window of
  // the period mode. The plan beside it has its own entry (further down).
  let compKeyStart = "";
  let compKeyEnd = "";
  // "custom" is treated the same as "none" for hydration: this client asks the
  // dashboard GET for "previous_year" or "previous_period" only, so
  // custom-range comparison data is never inline.
  if (ctx.comparisonMode === "previous_year" || ctx.comparisonMode === "previous_period") {
    const comp = item.comparison_cached_data_json as Record<string, unknown> | null;
    if (!comp) return "no_comparison_data";
    const cDateRange = comp.date_range as Record<string, unknown> | undefined;
    if (
      !cDateRange
      || typeof cDateRange.start_date !== "string"
      || typeof cDateRange.end_date !== "string"
    ) {
      return "bad_comparison_date_range";
    }
    const cRows = Array.isArray(comp.rows) ? (comp.rows as Record<string, unknown>[]) : [];
    comparisonShape = {
      rows: cRows,
      columns: Array.isArray(comp.columns) ? (comp.columns as string[]) : [],
      row_count: typeof comp.row_count === "number" ? comp.row_count : cRows.length,
      date_range: {
        start_date: cDateRange.start_date,
        end_date: cDateRange.end_date,
        timezone: typeof cDateRange.timezone === "string" ? cDateRange.timezone : "Europe/Amsterdam",
      },
      // What the total covers (ADR-0310), carried as it came: the merge
      // turns an `empty` total into the card's reason row and a `clipped`
      // one into its hover. NOT part of the key below, which stays the
      // requested window (`date_range`), so a clipped total still hits.
      ...(comp.coverage !== undefined ? { coverage: comp.coverage as ComparisonData["coverage"] } : {}),
      ...(comp.covered_range !== undefined
        ? { covered_range: comp.covered_range as ComparisonData["covered_range"] }
        : {}),
    };
    compKeyStart = cDateRange.start_date;
    compKeyEnd = cDateRange.end_date;
  }

  // Synthetic shape — typed superset of what cache readers consume.
  // setCachedRefresh strips comparison_data + comparison_error
  // (dashboardCache.ts:125); we don't include them here either.
  const synthetic = {
    chart_spec_json: item.chart_spec_json,
    chart_config_json: item.chart_config_json ?? null,
    data_json: cached,
    cached_data_json: cached as Record<string, unknown>,
    row_count: rows.length,
    row_count_truncated: false,
    last_refreshed_at: item.last_refreshed_at,
    // available_time_grains intentionally undefined — cache readers see
    // `cached.available_time_grains ?? []` (Insights.tsx:1010-1011) and
    // skip the grainSetsCollected intersection, preserving the initial
    // ALL_GRAINS state for the dropdown.
    available_time_grains: undefined,
    data_freshness: ctx.dataFreshness,
    cache_valid_until: ctx.cacheValidUntil,
  } as unknown as RefreshItemResponse;

  // One spec, one report — grainsToWrite writes the SAME spec under several
  // keys (grain-less items are written under every available grain), and a
  // per-key log would be five copies of one finding.
  reportIncoherentSpec("hydrateItem", item.id, item.chart_spec_json);

  // The plan beside the window (W1.8): an OPTIONAL second leg — absent, the
  // tile draws the window alone, so its absence is never a skip reason.
  let targetShape: ComparisonData | null = null;
  if (
    ctx.withTarget === true
    && (ctx.comparisonMode === "previous_year" || ctx.comparisonMode === "previous_period")
  ) {
    const tgt = (item.target_cached_data_json ?? null) as Record<string, unknown> | null;
    if (tgt) {
      const tRows = Array.isArray(tgt.rows) ? (tgt.rows as Record<string, unknown>[]) : [];
      const tDateRange = (tgt.date_range ?? {}) as Record<string, unknown>;
      targetShape = {
        rows: tRows,
        columns: Array.isArray(tgt.columns) ? (tgt.columns as string[]) : [],
        row_count: typeof tgt.row_count === "number" ? tgt.row_count : tRows.length,
        date_range: {
          start_date: typeof tDateRange.start_date === "string" ? tDateRange.start_date : cachedStart,
          end_date: typeof tDateRange.end_date === "string" ? tDateRange.end_date : cachedEnd,
          timezone: typeof tDateRange.timezone === "string" ? tDateRange.timezone : "Europe/Amsterdam",
        },
        target: (tgt.target as ComparisonData["target"]) ?? null,
      };
    }
  }

  // The legs expire with the main answer: a customer tile's comparison is
  // no fresher than its rows (managed: null, the 30-min default, as before).
  const legValidUntil = computeValidUntil(synthetic);
  for (const grain of grainsToWrite) {
    setCachedRefresh(item.id, cachedStart, cachedEnd, synthetic, grain);
    if (comparisonShape !== null) {
      setCachedComparison(
        item.id,
        cachedStart,
        cachedEnd,
        compKeyStart,
        compKeyEnd,
        comparisonShape,
        legValidUntil,
      );
    }
    if (targetShape !== null) {
      setCachedComparison(
        item.id, cachedStart, cachedEnd, TARGET_COMPARISON_KEY, TARGET_COMPARISON_KEY, targetShape,
        legValidUntil,
      );
    }
  }

  return null;
}


// ============================================================================
// PAGE-LEVEL CACHE ELIGIBILITY
// Plan: docs/plans-pending/2026-07-31-aggregate-dashboard-refresh-defects.md
// ============================================================================

/**
 * Fallback for `DashboardDTO.warmed_presets` when the backend predates the
 * field (frontend and backend deploy independently). Mirrors
 * services/dashboard_cache_config.py WARMED_PRESETS — used ONLY on that
 * rollout-skew path; the served list is authoritative.
 */
export const DEFAULT_WARMED_PRESETS = [
  "yesterday",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
] as const;

/**
 * Should we ask the backend for this tab's materialized page snapshot?
 *
 * A dashboard without a data source (account-aggregate, composite) can never
 * have one — the refill is DS-keyed and
 * `refill_aggregate_dashboard_for_account` explicitly does not assemble
 * snapshots. Asking anyway produced one 404 per tab per date-switch.
 *
 * The answer comes from the server (`page_snapshot_eligible`) rather than
 * from a client-side copy of that rule, so that building aggregate snapshots
 * later flips this on by itself instead of being silently suppressed here.
 */
export function isPageSnapshotEligible(
  dash: Pick<
    DashboardDTO,
    "is_managed" | "data_source" | "page_snapshot_eligible" | "warmed_presets"
  >,
  preset: string | undefined,
): boolean {
  if (!preset) return false;
  const eligible =
    dash.page_snapshot_eligible ?? (!!dash.data_source && dash.is_managed);
  if (!eligible) return false;
  const warmed = dash.warmed_presets ?? DEFAULT_WARMED_PRESETS;
  return (warmed as readonly string[]).includes(preset);
}

/**
 * Split a tab's items into the two groups whose rendered numbers do NOT
 * belong to the selected date range.
 *
 * Why this exists: the dashboard GET embeds each item's `cached_data_json`
 * — the LAST SERVED range, not the selected one (dashboard_routes.py:231) —
 * and the tiles paint it immediately. Until the refresh reconciles, the user
 * reads numbers from one period under another period's header.
 *
 * An item is stale when BOTH:
 *  a. the client cache cannot serve the selected range (hydration just ran,
 *     so a hit here means Phase 1 will paint the right numbers instantly and
 *     a spinner would be gratuitous flicker). "Can serve" mirrors Phase 1's
 *     all-or-nothing rule exactly: with a comparison range active, a main-cache
 *     hit WITHOUT the matching comparison entry still goes to the network, so
 *     it counts as stale here. Checking only the main cache would leave the
 *     default view (comparison_mode=previous_year) showing the previous
 *     range's numbers with no loading state at all. AND
 *  b. its `cached_date_range` is absent or different from the selection.
 *     Absent counts as stale — `refresh_dashboard_item` writes
 *     `cached_data_json` while setting `cached_date_range = None` when the IR
 *     has no dates (dashboard_refresh_service.py:1242), and those rows are
 *     painted all the same.
 *
 * Stale items split by what they should show instead:
 *  - `spinner`: the normal case — the tile takes the existing refreshing
 *    state until real data lands.
 *  - `blank`: items paused by the failure backoff. They keep their error
 *    badge and retry button (a spinner would replace both, and the
 *    reconciliation that would clear it only runs after the roundtrip), so
 *    their rows are dropped instead.
 */
export function computeStaleItemIds(
  dashboard: DashboardDTO,
  tabId: string | null,
  startDate: string,
  endDate: string,
  timeGrain?: string,
  /** Per tile, `comparisonLegCacheKeys(request, chartType)` of the selected
   *  range: every leg the refresh will ask for THIS tile — the plan beside
   *  the window (S2), and on a metric card the other period (S4). */
  legKeysFor: (item: DashboardItemDTO) => ReadonlyArray<{ start: string; end: string }> = () => [],
  /** A tile on a window of its OWN — a block with its own period (ADR-0445)
   *  — and that window's ISO dates and grain; null = the selected range. Its
   *  rows are judged against its window, never the header's. */
  windowFor: (item: DashboardItemDTO) => { start: string; end: string; grain: string } | null = () => null,
): { spinner: string[]; blank: string[] } {
  const spinner: string[] = [];
  const blank: string[] = [];
  if (!tabId) return { spinner, blank };

  for (const topic of dashboard.topics) {
    for (const tab of topic.tabs) {
      if (tab.id !== tabId) continue;
      for (const item of tab.items) {
        // Non-refreshable tiles never resolve to anything else — leave them.
        if (!item.query_ir_json && !item.query_sql) continue;
        const own = windowFor(item);
        const start = own?.start ?? startDate;
        const end = own?.end ?? endDate;
        const grain = own ? own.grain : timeGrain;
        // Same all-or-nothing check refreshAllItems' Phase 1 performs.
        const servable =
          !!getCachedRefresh(item.id, start, end, grain) &&
          comparisonLegsCached(item.id, start, end, legKeysFor(item));
        if (servable) continue;
        const cdr = item.cached_date_range;
        const matches =
          !!cdr && cdr.start_date === start && cdr.end_date === end;
        if (matches) continue;
        if (isRefreshBackedOff(item.id, start, end, grain)) {
          blank.push(item.id);
        } else {
          spinner.push(item.id);
        }
      }
    }
  }
  return { spinner, blank };
}
